import React from 'react';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { IconScout } from '@/components/ui/images/IconScout';
import { useTranslation } from '@/i18n';
import type { ManagedAttendanceRecord } from '@/api/domains/attendance';
import { SidePanelFormLayout } from '@/components/layout';


export interface ManagedAttendanceDetailProps {
  record: ManagedAttendanceRecord | null;
}

// Helper functions for translation logic
const getTranslateStatus = (translate: (key: string) => string, status: string): string => {
  const statusMap: Record<string, string> = {
    pending: translate('attendance:managedAttendance.status.pending'),
    approved: translate('attendance:managedAttendance.status.approved'),
    rejected: translate('attendance:managedAttendance.status.rejected'),
  };
  return statusMap[status] ?? status;
};

interface SubComponentProps {
  record: ManagedAttendanceRecord;
  translate: (key: string) => string;
  translateStatus: (status: string) => string;
}

const IncidentFields: React.FC<SubComponentProps> = ({
  record,
  translate,
  translateStatus,
}) => (
  <>
    <LabelInput
      label={translate('attendance:managedAttendance.detail.registrationDate')}
      value={record.creationDate}
      disabled
      iconRight={<IconScout name="calendarAlt" size="sm" />}
    />
    <LabelInput
      label={translate('attendance:managedAttendance.detail.location')}
      value={record.location}
      disabled
    />
    <LabelInput
      label={translate('attendance:managedAttendance.detail.eventType')}
      value={record.eventName || '-'}
      disabled
    />
    <LabelInput
      label={translate('attendance:managedAttendance.detail.errorType')}
      value={record.errorType}
      disabled
    />
    <LabelInput
      label={translate('attendance:managedAttendance.detail.markingDate')}
      value={record.workdayDate}
      disabled
      iconRight={<IconScout name="calendarAlt" size="sm" />}
    />
    <LabelInput
      label={translate('attendance:managedAttendance.detail.markingTime')}
      value={record.markingTime}
      disabled
      iconRight={<IconScout name="clockThree" size="sm" />}
    />
    <LabelInput
      label={translate('attendance:managedAttendance.detail.comment')}
      value={record.comment ?? '-'}
      disabled
    />
    <LabelInput
      label={translate('attendance:managedAttendance.detail.status')}
      value={translateStatus(record.status)}
      disabled
    />
    {record.status === 'rejected' && (
      <LabelInput
        label={translate('attendance:managedAttendance.detail.rejectionComment')}
        value={record.rejectionComment ?? '-'}
        disabled
      />
    )}
  </>
);

interface StandardFieldsProps extends SubComponentProps {
  showSimplifiedView: boolean;
  isModifiedMarking: boolean;
}

const StandardFields: React.FC<StandardFieldsProps> = ({
  record,
  translate,
  translateStatus,
  showSimplifiedView,
  isModifiedMarking,
}) => (
  <>
    {!showSimplifiedView && (
      <>
        <LabelInput
          label={translate('attendance:managedAttendance.detail.fullName')}
          value={record.fullName}
          disabled
        />
        <LabelInput
          label={translate('attendance:managedAttendance.detail.area')}
          value={record.area}
          disabled
        />
        <LabelInput
          label={translate('attendance:managedAttendance.detail.location')}
          value={record.location}
          disabled
        />
        <LabelInput
          label={translate('attendance:managedAttendance.detail.errorType')}
          value={record.errorType}
          disabled
        />
      </>
    )}
    <LabelInput
      label={translate('attendance:managedAttendance.detail.markingDate')}
      value={record.workdayDate}
      disabled
      iconRight={<IconScout name="calendarAlt" size="sm" />}
    />
    <LabelInput
      label={translate('attendance:managedAttendance.detail.markingTime')}
      value={record.markingTime}
      disabled
      iconRight={<IconScout name="clockThree" size="sm" />}
    />
    {isModifiedMarking && record.originalMarkTime && (
      <LabelInput
        label={translate('attendance:managedAttendance.detail.originalMarkingTime')}
        value={record.originalMarkTime}
        disabled
        iconRight={<IconScout name="clockThree" size="sm" />}
      />
    )}
    <LabelInput
      label={translate('attendance:managedAttendance.detail.eventType')}
      value={record.eventName || '-'}
      disabled
    />
    {showSimplifiedView && (
      <>
        <LabelInput
          label={translate('attendance:managedAttendance.detail.creatorUser')}
          value={record.creatorUser ?? '-'}
          disabled
        />
        <LabelInput
          label={translate('attendance:managedAttendance.detail.creationDate')}
          value={record.creationDate}
          disabled
          iconRight={<IconScout name="calendarAlt" size="sm" />}
        />
        <LabelInput
          label={translate('attendance:managedAttendance.detail.actionDate')}
          value={record.actionDate ?? '-'}
          disabled
          iconRight={<IconScout name="calendarAlt" size="sm" />}
        />
      </>
    )}
    {!showSimplifiedView && (
      <>
        <LabelInput
          label={translate('attendance:managedAttendance.detail.status')}
          value={translateStatus(record.status)}
          disabled
        />
        <LabelInput
          label={translate('attendance:managedAttendance.detail.comment')}
          value={record.comment ?? '-'}
          disabled
        />
      </>
    )}
    {record.status === 'rejected' && (
      <LabelInput
        label={translate('attendance:managedAttendance.detail.rejectionComment')}
        value={record.rejectionComment ?? '-'}
        disabled
      />
    )}
  </>
);

export const ManagedAttendanceDetail: React.FC<ManagedAttendanceDetailProps> = ({
  record,
}) => {
  const { t: translate } = useTranslation();

  if (!record) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        {translate('attendance:managedAttendance.messages.noData')}
      </div>
    );
  }

  const translateStatus = (status: string) => getTranslateStatus(translate, status);

  // ASIS-4218: Different fields for disabled markings
  // ASIS-4219: Different fields for modified markings
  // ASIS-4216: Different fields for incident markings
  const isDisabledMarking = record.action === 'disabled';
  const isModifiedMarking = record.action === 'modified';
  const isIncidentMarking = record.action === 'incident';
  const isCreatedFinalised = record.action === 'created' && (record.status === 'approved' || record.status === 'rejected');
  const showSimplifiedView = isDisabledMarking || isModifiedMarking || isCreatedFinalised;

  return (
    <SidePanelFormLayout data-testid="managed-attendance-detail-form">
      <SidePanelFormLayout.Fields>
        {/* ASIS-4216: Incident specific fields */}
        {isIncidentMarking ? (
          <IncidentFields
            record={record}
            translate={translate}
            translateStatus={translateStatus}
          />
        ) : (
          /* Standard fields for non-incident types */
          <StandardFields
            record={record}
            translate={translate}
            translateStatus={translateStatus}
            showSimplifiedView={showSimplifiedView}
            isModifiedMarking={isModifiedMarking}
          />
        )}
      </SidePanelFormLayout.Fields>
    </SidePanelFormLayout>
  );
};

export default ManagedAttendanceDetail;
